import { SEQUENCE_STATUS_IDS, STEPS_ORDINAL_START } from 'features/bulk-email/sequences/constants';
import strings from 'strings';
import { BulkMenuItems, BulkScopes } from 'UI/constants/defaults';
import { EntityType } from 'UI/constants/entityTypes';
import { getOrdinalSuffix } from 'UI/utils';

import { PREVIEW_BULK_RENDERERS } from './PreviewBulkRenderers';

const { mailMeta: MAIL_META_STRINGS } = strings.bulkEmails.preview;

export const booleanCopyRenderer = active =>
  active ? MAIL_META_STRINGS.booleanOptions.yes : MAIL_META_STRINGS.booleanOptions.no;

const getBlockResendCopy = (blockResend, blockDays) => {
  const blockResendCopy = booleanCopyRenderer(blockResend);
  if (!blockResend) return blockResendCopy;

  const blockDaysCopy = blockDays ? `(${blockDays} Days)` : `(no interval selected)`;

  return `${blockResendCopy} ${blockDaysCopy}`;
};

const checkIfDateHasPassed = sentDate => {
  const today = new Date();
  const sentOnDate = new Date(sentDate);

  return today > sentOnDate;
};

const mapSequencesMetaData = ({ sequences }) => {
  if (!sequences) return null;

  const today = new Date();
  const mappedMeta = sequences.map((attempt, index) => {
    const key = getOrdinalSuffix(index + STEPS_ORDINAL_START);
    const { scheduledAt: sentOn } = attempt;
    const { searchProject, created_by: createdBy } = attempt.emailHistory;
    const isAttemptCanceled = attempt.statusId === SEQUENCE_STATUS_IDS.canceled;

    return {
      bulkId: attempt.emailHistory.id,
      createdBy,
      delayDays: attempt.sendingAfterNextDays,
      html: attempt.emailHistory.emailBody.html,
      key,
      meta: [
        {
          title: MAIL_META_STRINGS.to.title,
          render:
            searchProject && checkIfDateHasPassed(sentOn)
              ? PREVIEW_BULK_RENDERERS.SearchProjects({
                  searchProject: {
                    ...searchProject,
                    bulkableItems: searchProject.total_items
                  }
                })
              : "Search Project created before send with recipients who didn't open the previous bulk",
          enabled: true
        },
        {
          title: MAIL_META_STRINGS.sendingOn.title,
          render: PREVIEW_BULK_RENDERERS.Date(sentOn),
          enabled: true
        }
      ],
      shouldEnableMenuButton: new Date(sentOn) > today && !isAttemptCanceled,
      shouldEnableStatsButton: new Date(sentOn) < today && !isAttemptCanceled,
      status: {
        id: attempt.statusId,
        title: attempt.statusTitle
      },
      stepId: attempt.id,
      subject: attempt.emailHistory.emailBody.subject
    };
  });

  return mappedMeta;
};

export const getMailMeta = ({ bulkInPreviewData, isSequencesActive = false, selectedMenuItem }) => {
  if (!bulkInPreviewData || !bulkInPreviewData.id) {
    return { mainMailMeta: [], secondaryMailMeta: [], sequencesMeta: null };
  }

  const {
    createdAt,
    emailBody = {},
    emailConfig = {},
    searchProject,
    sendDate,
    userInfo = {}
  } = bulkInPreviewData;

  const attachments = emailBody?.attachments || [];

  const {
    blockResend,
    blockDays,
    bulkType = {},
    generalCategory,
    isScheduled,
    marketing = {},
    recruiting = {},
    template = {}
  } = emailConfig;

  const { marketingCandidates = [], ...marketingConfig } = marketing;
  const { recruitingJobOrders = [], ...recruitingConfig } = recruiting;
  const templateName = template?.name;

  const mainMailMeta = [
    {
      title: MAIL_META_STRINGS.from.title,
      render: `${userInfo.name} - ${userInfo.email}`,
      enabled: true
    },
    {
      title: MAIL_META_STRINGS.to.title,
      render: PREVIEW_BULK_RENDERERS.SearchProjects({
        searchProject
      }),
      enabled: true
    },
    {
      title: MAIL_META_STRINGS.createdOn.title,
      render: PREVIEW_BULK_RENDERERS.Date(
        selectedMenuItem === BulkMenuItems.Drafts ? createdAt : sendDate
      ),
      enabled: true
    },
    {
      title: MAIL_META_STRINGS.bulkType.title,
      render: BulkScopes.ById[bulkType?.id] || MAIL_META_STRINGS.bulkType.defaultValue,
      enabled: true
    },
    {
      title: MAIL_META_STRINGS.generalCategory.title,
      render: generalCategory?.title || MAIL_META_STRINGS.generalCategory.defaultValue,
      enabled: bulkType?.id === BulkScopes.General
    },
    {
      title: MAIL_META_STRINGS.candidatesToMarket.title,
      render: PREVIEW_BULK_RENDERERS.BulkTypeEntities({
        entityType: EntityType.Candidate,
        entities: marketingCandidates
      }),
      enabled: bulkType?.id === BulkScopes.Marketing && marketingCandidates?.length > 0
    },
    {
      title: MAIL_META_STRINGS.jobOrdersToRecruit.title,
      render: PREVIEW_BULK_RENDERERS.BulkTypeEntities({
        entityType: EntityType.Joborder,
        entities: recruitingJobOrders
      }),
      enabled: bulkType?.id === BulkScopes.Recruiting && recruitingJobOrders?.length > 0
    },
    {
      title: MAIL_META_STRINGS.isSchedule.title,
      render: booleanCopyRenderer(isScheduled),
      enabled: selectedMenuItem === BulkMenuItems.Sent
    }
  ];

  const marketingConfigMeta = [
    {
      position: 1,
      title: MAIL_META_STRINGS.candidatesToMarket.config.blockHirings,
      value: marketingConfig.blockCompanies
    },
    {
      position: 2,
      title: MAIL_META_STRINGS.candidatesToMarket.config.blockAssociatedCompanies,
      value: marketingConfig.blockAssociatedCompanies
    },
    {
      position: 3,
      title: MAIL_META_STRINGS.candidatesToMarket.config.blockSignedClientCompanies,
      value: marketingConfig.blockClientCompanies
    },
    {
      position: 4,
      title: MAIL_META_STRINGS.candidatesToMarket.config.allowReachingCandidates,
      value: marketingConfig.allowMarketingCandidates
    }
  ];

  const orderedMarketingMeta = [...marketingConfigMeta.sort((a, b) => a.position - b.position)];

  const secondaryMailMeta = [
    ...orderedMarketingMeta.map(meta => ({
      ...meta,
      render: booleanCopyRenderer(meta.value),
      enabled: bulkType?.id === BulkScopes.Marketing
    })),
    {
      title: MAIL_META_STRINGS.jobOrdersToRecruit.config.allowReachingHirings,
      render: booleanCopyRenderer(recruitingConfig.allowRecruitHirings),
      enabled: bulkType?.id === BulkScopes.Recruiting
    },
    {
      title: MAIL_META_STRINGS.template.title,
      render: templateName,
      enabled: !!templateName
    },
    {
      title: MAIL_META_STRINGS.blockResend.title,
      render: getBlockResendCopy(blockResend, blockDays),
      enabled: true
    },
    {
      title: MAIL_META_STRINGS.attachments.title,
      render: PREVIEW_BULK_RENDERERS.Attachments({ attachments }),
      RowProps: {
        alignItems: 'flex-start'
      },
      enabled: attachments.length > 0
    }
  ];

  const sequencesMeta = isSequencesActive
    ? mapSequencesMetaData({
        sequences: bulkInPreviewData?.sequences?.entries
      })
    : null;

  return { mainMailMeta, secondaryMailMeta, sequencesMeta };
};
